// How recent a reading must be to count as "current". Sleep is handled by its
// own night-label logic below (an hours window mis-classifies last night late
// in the day), so it is intentionally absent here.
export const CURRENCY_WINDOW_HOURS = {
    "wearable.hrv": 20,
    "wearable.rhr": 20,
    "wearable.recovery": 20,
    // A morning BP reading is "this morning" through the day, stale by the next.
    "wearable.bp.systolic": 20,
    "wearable.bp.diastolic": 20,
    // Stress is a momentary daytime state; a reading hours old is not "now".
    "wearable.stress": 4,
    // Glucose moves fast (post-meal swings within an hour); a stale reading is not "now".
    "wearable.glucose": 4,
    "wearable.activity": 14,
    // A "today" total (steps, hydration) from a prior day is not today's.
    "hydration.ml": 18,
    "wearable.spo2": 26,
    "wearable.respiration": 26,
    // Weather shifts through the day; matches the lens's 4h freshness so a stale
    // reading cannot reframe the brief (CTX-3: "rain today" on a hot afternoon).
    "environment.weather_today": 4,
};
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;
// Fallback status-recency window for kinds without a canonical currency window.
export const DEFAULT_STATUS_RECENCY_HOURS = 48;
/**
 * Split the latest-reading map into fresh and stale kinds. Governed kinds use
 * their canonical currency window; ungoverned kinds fall back to
 * `fallbackHours`. The brief's stale-status lint and the shared co-occurrence
 * computation both gate on this split.
 */
export function splitSignalRecency(input, now, fallbackHours = DEFAULT_STATUS_RECENCY_HOURS) {
    const staleKinds = [];
    const freshKinds = [];
    for (const [kind, iso] of Object.entries(input.latestSignalAt ?? {})) {
        const at = Date.parse(iso);
        if (!Number.isFinite(at))
            continue;
        const windowHours = CURRENCY_WINDOW_HOURS[kind] ?? fallbackHours;
        (now.getTime() - at > windowHours * MS_PER_HOUR ? staleKinds : freshKinds).push(kind);
    }
    return { staleKinds, freshKinds };
}
export function isCurrent(kind, occurredAt, now) {
    const window = CURRENCY_WINDOW_HOURS[kind];
    if (window === undefined)
        return true; // ungoverned kinds (e.g. calendar) are not recency-gated here
    return now.getTime() - occurredAt.getTime() <= window * MS_PER_HOUR;
}
/**
 * Whether a reading is current enough to show as a raw value in a prompt. Now-
 * metrics use their currency window; sleep uses the night label (an hours window
 * mis-reads "last night" late in the day); every other kind is ungated here.
 * Stale readings are excluded from the prompt and replaced by an honesty note,
 * so the model can never attach a real value (8.1 hours) to the wrong time
 * ("last night"). One predicate so every agent gates the same way.
 */
export function isCurrentReading(kind, occurredAt, now, timezone) {
    if (kind === "wearable.sleep") {
        return sleepNightLabel(occurredAt, now, timezone ?? "UTC").isLastNight;
    }
    return isCurrent(kind, occurredAt, now);
}
/** The compact "ago" ladder every surface shares ("just now", "12m ago",
 *  "3h ago", "2d ago"). Prose voice stays in describeAge. */
export function compactAgeLabel(fromMs, toMs) {
    const min = Math.max(0, (toMs - fromMs) / 60_000);
    if (min < 2)
        return "just now";
    if (min < 60)
        return `${Math.round(min)}m ago`;
    if (min < 105)
        return "1h ago";
    if (min < 36 * 60)
        return `${Math.round(min / 60)}h ago`;
    const days = min / 1440;
    if (days < 7)
        return `${Math.round(days)}d ago`;
    if (days < 35)
        return `${Math.round(days / 7)}w ago`;
    return `${Math.round(days / 30)}mo ago`;
}
export function describeAge(fromMs, toMs) {
    const h = (toMs - fromMs) / MS_PER_HOUR;
    if (h < 1)
        return "under an hour ago";
    if (h < 36) {
        const n = Math.round(h);
        return `about ${n} hour${n === 1 ? "" : "s"} ago`;
    }
    const d = Math.round(h / 24);
    return `about ${d} day${d === 1 ? "" : "s"} ago`;
}
// Building an Intl.DateTimeFormat costs about forty microseconds and every sleep label makes three, so each zone's formatters are built once and kept.
const FORMATTERS = new Map();
function formatterFor(key, build) {
    const cached = FORMATTERS.get(key);
    if (cached !== undefined)
        return cached;
    let formatter = null;
    try {
        formatter = build();
    }
    catch {
        formatter = null;
    }
    if (FORMATTERS.size >= 200)
        FORMATTERS.clear();
    FORMATTERS.set(key, formatter);
    return formatter;
}
function localDateKey(d, tz) {
    const f = formatterFor(`date:${tz}`, () => new Intl.DateTimeFormat("en-CA", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }));
    // Formatting throws too, not just construction: an unparseable occurredAt
    // arrives here as an Invalid Date and every Intl call on one is a RangeError.
    try {
        if (f)
            return f.format(d);
    }
    catch {
        // fall through
    }
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}
function dayDiff(aKey, bKey) {
    const a = Date.parse(`${aKey}T00:00:00Z`);
    const b = Date.parse(`${bKey}T00:00:00Z`);
    return Math.round((a - b) / MS_PER_DAY);
}
function weekday(key) {
    // UTC, because `key` is already a local calendar date.
    const f = formatterFor("weekday:UTC", () => new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "long" }));
    try {
        if (f)
            return f.format(new Date(`${key}T12:00:00Z`));
    }
    catch {
        // fall through
    }
    return "an earlier night";
}
/** The night a bedtime belongs to. */
export function sleepNightKey(bedtime, tz) {
    const bedKey = localDateKey(bedtime, tz);
    return localHourIn(bedtime, tz) < 12 ? shiftDateKey(bedKey, -1) : bedKey;
}
/** The night a reading belongs to, by weekday, so it stays true after composition. */
export function sleepNightName(bedtime, tz) {
    return `${weekday(sleepNightKey(bedtime, tz))} night`;
}
/**
 * What night a sleep reading represents, relative to `now`. `occurredAt` is the
 * night's start (bedtime), so the night ends the morning after. Returns the
 * honest label: "last night" only when it truly is.
 */
export function sleepNightLabel(bedtime, now, tz) {
    const eveningKey = sleepNightKey(bedtime, tz);
    const todayKey = localDateKey(now, tz);
    // The night that started on eveningKey ends the next morning.
    const nightsAgo = dayDiff(todayKey, eveningKey) - 1;
    if (nightsAgo <= 0)
        return { label: "last night", isLastNight: true };
    if (nightsAgo === 1)
        return { label: "the night before last", isLastNight: false };
    return { label: `${weekday(eveningKey)} night`, isLastNight: false };
}
function localHourIn(d, tz) {
    const f = formatterFor(`hour:${tz}`, () => new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", hourCycle: "h23" }));
    if (!f)
        return 12;
    try {
        const h = f.formatToParts(d).find((p) => p.type === "hour")?.value;
        return h ? Number.parseInt(h, 10) : 12;
    }
    catch {
        return 12;
    }
}
function shiftDateKey(key, deltaDays) {
    return new Date(Date.parse(`${key}T00:00:00Z`) + deltaDays * MS_PER_DAY)
        .toISOString()
        .slice(0, 10);
}
function latestByKind(signals) {
    const m = new Map();
    for (const s of signals) {
        const cur = m.get(s.kind);
        if (!cur || s.occurredAt.getTime() > cur.occurredAt.getTime())
            m.set(s.kind, s);
    }
    return m;
}
const LABEL = {
    "wearable.hrv": "recovery (HRV)",
    "wearable.rhr": "resting heart rate",
    "wearable.recovery": "recovery",
    "wearable.bp.systolic": "morning blood pressure",
    "wearable.bp.diastolic": "morning blood pressure",
    "wearable.stress": "stress",
    "wearable.activity": "today's activity",
    "wearable.spo2": "blood oxygen",
    "wearable.respiration": "breathing rate",
    "environment.weather_today": "today's weather",
    "wearable.glucose": "glucose",
};
/**
 * Honesty lines for the signals block: one per wearable metric whose latest
 * reading is outside its currency window, plus a sleep line when the most
 * recent night is not last night. These tell the agent, deterministically, not
 * to present stale data as current.
 */
export function staleSignalNotes(signals, now, timezone) {
    const tz = timezone ?? "UTC";
    const latest = latestByKind(signals);
    const notes = [];
    // One note per base label: "recovery (HRV)" and "recovery" both stale would
    // read back as "your recovery and recovery" in generated prose.
    const notedBaseLabels = new Set();
    for (const kind of Object.keys(CURRENCY_WINDOW_HOURS)) {
        const s = latest.get(kind);
        if (!s || isCurrent(kind, s.occurredAt, now))
            continue;
        const label = LABEL[kind] ?? kind;
        const baseLabel = label.replace(/\s*\(.*\)$/, "");
        if (notedBaseLabels.has(baseLabel))
            continue;
        notedBaseLabels.add(baseLabel);
        const age = describeAge(s.occurredAt.getTime(), now.getTime());
        notes.push(`- ${label} is NOT current: last reading ${age}. Treat current ${label} as unknown; do not describe it as low, high, or "right now".`);
    }
    const sleep = latest.get("wearable.sleep");
    if (sleep) {
        const { isLastNight } = sleepNightLabel(sleep.occurredAt, now, tz);
        if (!isLastNight) {
            // Named, not relative: this note sits beside rows that name the night, and
            // it used to teach the phrase it was trying to forbid.
            notes.push(`- the most recent sleep reading is from ${sleepNightName(sleep.occurredAt, tz)}. There is no reading for the night just gone; do not present it as the member's latest night.`);
        }
    }
    return notes;
}
const NUMBER = /\b(?:\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\b/i;
const LAST_NIGHT = /\blast night\b/i;
const SLEEP_WORD = /\b(?:sleep|slept|rest(?:ed)?)\b/i;
const RECOVERY_WORD = /\b(?:recovery|hrv|heart[- ]rate variability)\b/i;
const RECOVERY_STATE = /\b(?:low|lower|lowered|high|higher|down|elevated|dropp?ed|dip|poor|weak)\b/i;
const ACTIVITY_WORD = /\b(?:steps?|stepped|walk(?:ed|ing)?|activity|active|move(?:d|ment)?)\b/i;
const TODAY_WORD = /\b(?:today|this morning|so far)\b/i;
const ACTIVITY_STATE = /\b(?:more|fewer|less|up|down|above|below|higher|lower|ahead|behind|than usual)\b/i;
const IMMEDIACY = /\b(?:right now|today|this morning|currently|at the moment)\b/i;
// Narrow "no data" negations: the sentence is an honest acknowledgement, not a
// claim. Deliberately excludes bare "not" so "slept eight hours, not bad" still
// counts as an assertion and is stripped.
const NO_DATA = /\b(?:no|without|isn't|wasn't|aren't|didn't|don't have|hasn't|can't|cannot|couldn't|won't|unavailable|unknown|missing|lacks?)\b/i;
const STALE_HINT = /\b(?:old|two nights|night before last|days? ago|hours? ago)\b/i;
function splitSentences(text) {
    return text.split(/(?<=[.!?])\s+/);
}
// gpt-oss emits curly apostrophes; normalise so hedge matching is reliable.
function normApostrophes(s) {
    return s.replace(/[‘’ʼ]/g, "'");
}
/** Drop sentences that present stale data as current. Returns kept text + drops. */
export function stripStaleImmediacy(text, ctx) {
    const hrvStale = ctx.staleFamilies.has("wearable.hrv") || ctx.staleFamilies.has("wearable.recovery");
    const dropped = [];
    const kept = splitSentences(text).filter((original) => {
        const s = normApostrophes(original);
        const honest = NO_DATA.test(s) || STALE_HINT.test(s);
        // 1. A sleep-hours claim tied to "last night" when sleep is not last night's.
        if (!honest &&
            !ctx.sleepIsLastNight &&
            LAST_NIGHT.test(s) &&
            SLEEP_WORD.test(s) &&
            NUMBER.test(s)) {
            dropped.push(original.trim());
            return false;
        }
        // 2. A recovery/HRV state-or-now claim when recovery data is stale.
        if (!honest &&
            hrvStale &&
            RECOVERY_WORD.test(s) &&
            (RECOVERY_STATE.test(s) || IMMEDIACY.test(s))) {
            dropped.push(original.trim());
            return false;
        }
        // 3. A day-scoped activity claim when activity is not from the member's local today.
        if (!honest &&
            !ctx.activityIsToday &&
            ACTIVITY_WORD.test(s) &&
            TODAY_WORD.test(s) &&
            (ACTIVITY_STATE.test(s) || (NUMBER.test(s) && /\bsteps?\b/i.test(s)))) {
            dropped.push(original.trim());
            return false;
        }
        return true;
    });
    return {
        text: kept
            .join(" ")
            .replace(/\s{2,}/g, " ")
            .trim(),
        dropped,
    };
}
/** Human-readable relative date label for completion history entries.
 *  "Today", "Yesterday", "3 days ago", "Last week", "2 weeks ago", "Last month", "3 months ago".
 *  Shared by the web and mobile completion-history components. */
export function formatCompletionDate(iso, now = new Date()) {
    const date = new Date(iso);
    // Compare local calendar days, not 24-hour UTC periods. A completion
    // made at 11pm local time must show "Yesterday" at 1am the next local
    // morning, even though only 2 hours elapsed. Using Math.floor on raw
    // milliseconds would return 0 (= "Today") in that case.
    const diffDays = Math.round((startOfDay(now).getTime() - startOfDay(date).getTime()) / 86_400_000);
    if (diffDays <= 0)
        return "Today";
    if (diffDays === 1)
        return "Yesterday";
    if (diffDays < 7)
        return `${diffDays} days ago`;
    if (diffDays < 14)
        return "Last week";
    if (diffDays < 30)
        return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60)
        return "Last month";
    return `${Math.floor(diffDays / 30)} months ago`;
}
function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
/** Human-readable relative date label for items added to the library.
 *  "today", "yesterday", "N days ago", "last week".
 *  Shared by the web and mobile library components. */
export function formatAddedDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return "recently";
    const today = new Date();
    const diffMs = startOfDay(today).getTime() - startOfDay(date).getTime();
    const diffDays = Math.round(diffMs / 86_400_000);
    if (diffDays <= 0)
        return "today";
    if (diffDays === 1)
        return "yesterday";
    if (diffDays < 7)
        return `${diffDays} days ago`;
    return "last week";
}
//# sourceMappingURL=recency.js.map